/**
 * Roles matrix API — cross-org ROLE availability (migration 0080).
 *
 * REUSES chaos.role_publications (no redundant table): a publication row =
 * "global/template role X is PUBLISHED (made available) in org Y"; the all-orgs
 * sentinel (org_id IS NULL server-side) = available in EVERY org of the
 * account. The matrix = which roles are PUBLISHED/AVAILABLE in which orgs.
 *
 * MIRRORS skillsApi (0078) / templatesApi (0079) EXACTLY, with ONE addition:
 * each row carries `ownerOrgId`. GLOBAL/template roles (ownerOrgId === null)
 * are the cross-org-publishable rows. ORG-SCOPED roles (ownerOrgId === <uuid>)
 * belong to that org and are NOT cross-publishable — the matrix renders them
 * read-only with their owning-org cell locked. FOUNDER-RESOLVED semantics
 * (pure checkbox): All-orgs → everywhere; specific org rows → those; no rows →
 * available nowhere. Publication is the availability gate; the in-org
 * assignment/grant layer is UNTOUCHED.
 *
 * Account-owner gated (GET /api/roles/all → 403 otherwise). Mirrors
 * apps/chaos-gateway/src/org-structure.ts.
 */
import type { RoleListItem } from '@chaos/types/org-structure';

import { apiJson } from './client';
import { normalizeOrgPublications } from './org-publication-shape';

// ── Cross-org publication matrix (0080) ────────────────────────────────
/**
 * WHERE an item is published, and which of those marks the "All organizations"
 * select-all button put there (migration 0168).
 *
 *   orgIds       — every organization the item is published into. THE list.
 *   viaAllOrgIds — the subset select-all placed (⊆ orgIds). The origin is
 *                  remembered because two rules depend on it: a new solo
 *                  Workspace inherits these marks, and the first other person
 *                  added to a Workspace switches them off. Marks placed by hand
 *                  are never touched by either.
 *
 * There is no `allOrgs` boolean any more. "Available everywhere" used to be a
 * separate STATUS row (org_id IS NULL) which made it impossible to exclude a
 * single organization — and which, because no read gate carried an account
 * filter, actually meant "every organization that will ever exist, on any
 * account". It is now simply the state where every organization is ticked,
 * which the UI reads off `orgIds` and therefore cannot disagree about.
 */
export interface RolePublications {
  orgIds: string[];
  viaAllOrgIds: string[];
}

// One column of the cross-org matrix — an org of the caller's account.
export interface RoleMatrixOrg {
  id: string;
  displayName: string;
  logoUrl: string | null;
}

// One row of the matrix (GET /api/roles/all): the role as a LIST ITEM plus its
// publication set and its owning org. ownerOrgId === null → GLOBAL/template
// role (cross-org-publishable). ownerOrgId === <uuid> → ORG-SCOPED role
// (read-only, owning-org cell locked). For org-scoped roles `publications` is
// always { orgIds:[], viaAllOrgIds:[] } (they never live in role_publications).
//
// ── IT IS A LIST ITEM AND NOT A ROLE (2026-08-12) ──────────────────────────
// This said `extends Role` while the gateway spread every column of
// chaos.roles, prompt included, into the wire — an account-owner-gated route,
// which on this installation meant the SECOND account owner read the first
// one's entire catalogue. The route now answers `roleListItemFrom`'s allowlist:
// no `systemPrompt`, no `instructions`, no permission mode / allowed tools /
// VPS access / autonomy, no view scope or auto-approve authority.
//
// THE TYPE CHANGE IS THE POINT, not bookkeeping: anything that needs a field
// this shape lacks must fetch the role by id (`GET /api/orgs/:orgId/roles/
// :roleId`, or `/api/global-roles/:roleId` for a template) and the compiler now
// says so at the call site instead of handing it `undefined` at runtime.
export interface RoleMatrixRow extends RoleListItem {
  ownerOrgId: string | null;
  publications: RolePublications;
}

export interface RoleMatrix {
  roles: RoleMatrixRow[];
  orgs: RoleMatrixOrg[];
}

interface RoleMatrixResponse {
  roles: RoleMatrixRow[];
  orgs: RoleMatrixOrg[];
}

interface RolePublicationsResponse {
  publications: RolePublications;
}

export const rolesMatrixApi = {
  // Account-wide matrix: every role (global + org-scoped) with its cross-org
  // publication set, plus the account's orgs as the matrix columns.
  // Account-owner only (403 otherwise). Mirrors skillsApi.listAll().
  //
  // THE PUBLICATION SETS ARE NORMALISED AT THE DOOR (see
  // ./org-publication-shape). Two reasons this one matters more than its four
  // siblings. It feeds <CrossOrgMatrix>, so it shares the Roles screen's fate
  // with connectors/templates/permissions; and `useIsAccountOwner()` in
  // lib/active-org.tsx probes THIS route for the whole shell, so it is the one
  // publication read that fires outside Settings.
  //
  // The org-scoped rows are where the wire is least like the type: an
  // org-scoped role never lives in `role_publications` at all, so its
  // publication set is whatever the gateway chooses to synthesise for a row
  // that has none. Today that is `{ orgIds: [], viaAllOrgIds: [] }`; the type
  // says it always will be, and the type is not on the wire.
  listAll(): Promise<RoleMatrix> {
    return apiJson<RoleMatrixResponse>(`/roles/all`).then((r) => ({
      roles: r.roles.map((row) => ({
        ...row,
        publications: normalizeOrgPublications(row.publications),
      })),
      orgs: r.orgs,
    }));
  },
  // Reconcile the FULL publication set for a global role in one call. Mirrors
  // skillsApi.setPublications().
  setPublications(roleId: string, pub: RolePublications): Promise<RolePublications> {
    return apiJson<RolePublicationsResponse>(`/roles/${roleId}/publications`, {
      method: 'PUT',
      json: { orgIds: pub.orgIds },
    }).then((r) => r.publications);
  },
  // Toggle ONE matrix cell ON. orgId=null = the all-organizations sentinel.
  // Tick ONE organization. 0168: `orgId: null` used to mean the sentinel and is
  // now a 400 — selectAll() is the route for that.
  addPublication(roleId: string, orgId: string): Promise<RolePublications> {
    return apiJson<RolePublicationsResponse>(`/roles/${roleId}/publications`, {
      method: 'POST',
      json: { orgId },
    }).then((r) => r.publications);
  },

  // THE SELECT-ALL. Puts a separate mark on every organization of the account;
  // any one of them can then be unticked on its own.
  selectAllOrgs(roleId: string): Promise<RolePublications> {
    return apiJson<RolePublicationsResponse>(`/roles/${roleId}/publications/all`, {
      method: 'POST',
    }).then((r) => r.publications);
  },

  // The same box, unticked. IT REMOVES ONLY THE MARKS IT MADE — hand-placed
  // marks survive, which is why the header box can end up half-selected.
  clearAllOrgs(roleId: string): Promise<RolePublications> {
    return apiJson<RolePublicationsResponse>(`/roles/${roleId}/publications/all`, {
      method: 'DELETE',
    }).then((r) => r.publications);
  },
  // Toggle ONE matrix cell OFF. orgId=null = remove the all-orgs sentinel.
  // Untick ONE organization. Works the same whether the mark was hand-placed or
  // put there by select-all — "after that, any one of them can be unticked" is
  // the whole point of 0168.
  removePublication(roleId: string, orgId: string): Promise<RolePublications> {
    return apiJson<RolePublicationsResponse>(
      `/roles/${roleId}/publications?orgId=${encodeURIComponent(orgId)}`,
      { method: 'DELETE' },
    ).then((r) => r.publications);
  },
  // Convenience: flip one cell to the desired state. Mirrors togglePublication().
  // Flip one matrix cell. `orgId === null` is the HEADER box (select-all), which
  // since 0168 is a real bulk write rather than a status flag.
  togglePublication(
    roleId: string,
    orgId: string | null,
    on: boolean,
  ): Promise<RolePublications> {
    if (orgId === null) {
      return on ? this.selectAllOrgs(roleId) : this.clearAllOrgs(roleId);
    }
    return on ? this.addPublication(roleId, orgId) : this.removePublication(roleId, orgId);
  },
};

export const rolesMatrixKeys = {
  all: ['roles-matrix'] as const,
  matrix: () => [...rolesMatrixKeys.all, 'matrix'] as const,
};
